<app-page-breadcrumb pageTitle="Plantillas de Invitación"></app-page-breadcrumb>

<div class="space-y-4">

  <!-- ── Controls bar ─────────────────────────────────────────────────────── -->
  <div class="flex items-center justify-between gap-2 flex-wrap">
    <p class="text-sm text-gray-500 dark:text-gray-400">
      Gestiona las plantillas de correo utilizadas para enviar invitaciones a nuevos usuarios.
    </p>
    <app-button variant="primary" (btnClick)="nuevaPlantilla()">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      Nueva plantilla
    </app-button>
  </div>

  <!-- ── Success banner ───────────────────────────────────────────────────── -->
  @if (successMessage) {
  <div class="p-3 bg-success-50 border border-success-200 rounded-lg dark:bg-success-500/10 dark:border-success-500/20 flex items-center gap-3">
    <svg class="w-5 h-5 text-success-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <p class="text-sm text-success-700 dark:text-success-400">{{ successMessage }}</p>
  </div>
  }

  <!-- ── Error state ───────────────────────────────────────────────────────── -->
  @if (error) {
  <div class="p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
    <div class="flex items-center gap-3">
      <svg class="w-5 h-5 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  </div>
  }

  <!-- ── Loading ───────────────────────────────────────────────────────────── -->
  @if (loading) {
  <div class="flex justify-center items-center py-12">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
    <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando plantillas...</span>
  </div>
  }

  <!-- ── Table ─────────────────────────────────────────────────────────────── -->
  @if (!loading) {
  <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="max-w-full overflow-x-auto custom-scrollbar">
      <table class="min-w-full">
        <thead class="border-b border-gray-100 dark:border-white/[0.05]">
          <tr>
            <th class="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">Nombre</th>
            <th class="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">Asunto</th>
            <th class="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">Estilo</th>
            <th class="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">Default</th>
            <th class="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">Fecha creación</th>
            <th class="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-white/[0.05]">

          @if (templates.length === 0) {
          <tr>
            <td colspan="6" class="px-5 py-12 text-center">
              <div class="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>No hay plantillas de invitación registradas</span>
                <button type="button" (click)="nuevaPlantilla()"
                  class="mt-1 text-sm text-brand-500 hover:underline">
                  Crear la primera plantilla
                </button>
              </div>
            </td>
          </tr>
          }

          @for (tpl of templates; track tpl.id) {
          <tr>
            <!-- Nombre -->
            <td class="px-5 py-3 text-start text-theme-sm">
              <div class="flex items-center gap-2">
                <span class="font-medium text-gray-800 dark:text-white/90">{{ tpl.name }}</span>
                @if (!tpl.isActive) {
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  Inactiva
                </span>
                }
              </div>
            </td>

            <!-- Asunto -->
            <td class="px-5 py-3 text-start text-theme-sm text-gray-500 dark:text-gray-400">
              <span class="truncate max-w-xs block" title="{{ tpl.subject }}">{{ tpl.subject }}</span>
            </td>

            <!-- Estilo -->
            <td class="px-5 py-3 text-start text-theme-sm">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {{ stylePresetColor(tpl.stylePreset) }}">
                {{ stylePresetLabel(tpl.stylePreset) }}
              </span>
            </td>

            <!-- Default -->
            <td class="px-5 py-3 text-start text-theme-sm">
              @if (tpl.isDefault) {
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400">
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                Default
              </span>
              } @else {
              <span class="text-gray-400 dark:text-gray-600 text-xs">—</span>
              }
            </td>

            <!-- Fecha creación -->
            <td class="px-5 py-3 text-start text-theme-sm text-gray-500 dark:text-gray-400">
              {{ formatDate(tpl.createdAt) }}
            </td>

            <!-- Acciones -->
            <td class="px-5 py-3 text-center text-theme-sm">
              @if (confirmDeleteId === tpl.id) {
              <div class="flex items-center justify-center gap-2">
                <span class="text-xs text-gray-600 dark:text-gray-400">¿Eliminar?</span>
                <button type="button" (click)="confirmDelete()" [disabled]="deleteLoading"
                  class="px-2 py-1 text-xs rounded-md bg-error-500 text-white hover:bg-error-600 transition-colors disabled:opacity-50">
                  @if (deleteLoading) {
                  <span class="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  } @else { Sí }
                </button>
                <button type="button" (click)="cancelDelete()"
                  class="px-2 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  No
                </button>
              </div>
              } @else {
              <div class="flex items-center justify-center gap-1">
                <!-- Editar -->
                <button type="button" (click)="editarPlantilla(tpl)"
                  class="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-white/[0.05] transition-colors text-gray-400 dark:text-gray-500"
                  title="Editar plantilla">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>

                <!-- Establecer por defecto -->
                @if (!tpl.isDefault) {
                <button type="button" (click)="establecerPorDefecto(tpl)"
                  [disabled]="setDefaultLoading === tpl.id"
                  class="rounded-lg p-2 hover:bg-success-50 hover:text-success-600 dark:hover:bg-success-500/10 dark:hover:text-success-400 transition-colors text-gray-400 dark:text-gray-500 disabled:opacity-50"
                  title="Establecer como plantilla por defecto">
                  @if (setDefaultLoading === tpl.id) {
                  <span class="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                  } @else {
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  }
                </button>
                }

                <!-- Eliminar -->
                <button type="button" (click)="askDelete(tpl.id)"
                  class="rounded-lg p-2 hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-500/10 dark:hover:text-error-400 transition-colors text-gray-400 dark:text-gray-500"
                  title="Eliminar plantilla">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              }
            </td>
          </tr>
          }

        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    @if (totalPages > 1) {
    <div class="border-t border-gray-100 px-5 py-4 dark:border-white/[0.05]">
      <div class="flex items-center justify-between">
        <div class="text-sm text-gray-500 dark:text-gray-400">
          Página {{ currentPageDisplay }} de {{ totalPages }} ({{ totalElements }} plantillas)
        </div>
        <div class="flex gap-2">
          <app-button variant="outline" size="sm" (btnClick)="changePage(currentPageDisplay - 1)" [disabled]="!hasPreviousPage">
            Anterior
          </app-button>
          <app-button variant="outline" size="sm" (btnClick)="changePage(currentPageDisplay + 1)" [disabled]="!hasNextPage">
            Siguiente
          </app-button>
        </div>
      </div>
    </div>
    }

  </div>
  }

</div>
